import React, { useEffect, useState } from "react";
import { hubExt, hubApi, PLAN_PRICING } from "../api.js";
import { toast } from "react-toastify";
import { useIsMobile } from "../hooks/useIsMobile.js";

const TYPE_LABEL = { restaurant:"Restaurant", coffee:"Coffee Shop", beach_bar:"Beach Bar", bar:"Bar & Pub", bistro:"Bistro", pizzerie:"Pizzerie", fast_food:"Fast Food", bakery:"Brutărie", hotel:"Hotel", food_truck:"Food Truck" };

export default function ClientAccountsPage() {
  const isMobile = useIsMobile();
  const [accounts, setAccounts]         = useState([]);
  const [restaurants, setRestaurants]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selected, setSelected]         = useState(null);
  const [linkId, setLinkId]             = useState("");
  const [saving, setSaving]             = useState(false);
  const [search, setSearch]             = useState("");

  const load = async () => {
    try {
      const [accRes, restRes] = await Promise.all([
        hubExt.getClientAccounts(),
        hubApi.getAll(),
      ]);
      setAccounts(accRes.data.accounts || []);
      setRestaurants(restRes.data.restaurants || []);
    } catch { toast.error("Eroare la încărcare."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSelect = (acc) => {
    setSelected(acc);
    setLinkId(acc.restaurantId?._id || "");
  };

  const handleActivate = async (isActive) => {
    if (!selected) return;
    setSaving(true);
    try {
      const payload = { isActive };
      if (linkId) payload.restaurantId = linkId;
      const { data } = await hubExt.patchClientAccount(selected._id, payload);
      toast.success(isActive ? "Cont activat!" : "Cont dezactivat.");
      setSelected(data.account);
      await load();
    } catch { toast.error("Eroare."); }
    finally { setSaving(false); }
  };

  const handleLink = async () => {
    if (!selected || !linkId) return;
    setSaving(true);
    try {
      const { data } = await hubExt.patchClientAccount(selected._id, { restaurantId: linkId, isActive: true });
      toast.success("Restaurant linkat și cont activat!");
      setSelected(data.account);
      await load();
    } catch { toast.error("Eroare la linkare."); }
    finally { setSaving(false); }
  };

  const filtered = accounts.filter(a =>
    !search ||
    a.restaurantName.toLowerCase().includes(search.toLowerCase()) ||
    a.email.toLowerCase().includes(search.toLowerCase()) ||
    a.fullName.toLowerCase().includes(search.toLowerCase())
  );

  const planInfo = (plan) => PLAN_PRICING[plan] || PLAN_PRICING[1];

  return (
    <div style={{ padding: isMobile ? "16px" : "32px 36px", maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.4px" }}>Conturi clienți</h1>
        <p style={{ fontSize: 14, color: "#64748b", marginTop: 4 }}>Gestionează conturile create din fluxul de înregistrare de pe orderly.ro</p>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3,1fr)", gap: isMobile ? 10 : 16, marginBottom: 20 }}>
        {[
          { label: "Total conturi", value: accounts.length, color: "#1d4ed8" },
          { label: "Active", value: accounts.filter(a => a.isActive).length, color: "#16a34a" },
          { label: "Neactivate", value: accounts.filter(a => !a.isActive).length, color: "#d97706" },
        ].map(s => (
          <div key={s.label} style={{ background: "white", borderRadius: 12, border: "1.5px solid #f1f5f9", padding: "18px 22px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 }}>{s.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.color, letterSpacing: "-1px" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <input value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Caută după restaurant, email sau nume..."
        style={{ width: "100%", height: 44, border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "0 16px", fontSize: 14, fontFamily: "inherit", outline: "none", marginBottom: 16, boxSizing: "border-box", background: "white" }}
      />

      <div style={{ display: "grid", gridTemplateColumns: selected && !isMobile ? "1fr 400px" : "1fr", gap: 20 }}>
        {/* Table */}
        <div style={{ background: "white", borderRadius: 14, border: "1.5px solid #f1f5f9", overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Se încarcă...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 48, textAlign: "center", color: "#94a3b8" }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>👥</div>
              <div style={{ fontWeight: 700, color: "#475569" }}>Niciun cont înregistrat</div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
              <thead>
                <tr style={{ borderBottom: "1.5px solid #f1f5f9" }}>
                  {["Restaurant", "Contact", "Plan", "Tip", "Status", "Linkat la"].map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(acc => {
                  const pi = planInfo(acc.plan);
                  const isActive = selected?._id === acc._id;
                  return (
                    <tr key={acc._id} onClick={() => handleSelect(acc)} style={{
                      borderBottom: "1px solid #f8fafc", cursor: "pointer",
                      background: isActive ? "#eff6ff" : "transparent",
                      transition: "background .12s",
                    }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#f8fafc"; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                    >
                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>{acc.restaurantName}</div>
                        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{acc.country} · {acc.currency}</div>
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{acc.fullName}</div>
                        <div style={{ fontSize: 12, color: "#94a3b8" }}>{acc.email}</div>
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 100, background: pi.bg, color: pi.color, border: `1px solid ${pi.border}` }}>
                          {pi.name}
                        </span>
                      </td>
                      <td style={{ padding: "14px 16px", fontSize: 12, color: "#64748b" }}>{TYPE_LABEL[acc.restaurantType] || acc.restaurantType}</td>
                      <td style={{ padding: "14px 16px" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 100,
                          background: acc.isActive ? "#f0fdf4" : "#fff1f2",
                          color: acc.isActive ? "#16a34a" : "#e11d48",
                        }}>
                          {acc.isActive ? "Activ" : "Inactiv"}
                        </span>
                      </td>
                      <td style={{ padding: "14px 16px", fontSize: 12, color: acc.restaurantId ? "#1d4ed8" : "#94a3b8" }}>
                        {acc.restaurantId ? (acc.restaurantId.name || "Linkat") : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div style={{ background: "white", borderRadius: 14, border: "1.5px solid #f1f5f9", padding: 24, position: "sticky", top: 20, maxHeight: "80vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", margin: 0 }}>{selected.restaurantName}</h3>
              <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 18 }}>×</button>
            </div>

            <InfoRow label="Nume complet" value={selected.fullName} />
            <InfoRow label="Email" value={selected.email} />
            <InfoRow label="Username" value={selected.username} />
            <InfoRow label="Tip local" value={TYPE_LABEL[selected.restaurantType] || selected.restaurantType} />
            <InfoRow label="Țară" value={selected.country} />
            <InfoRow label="Monedă" value={selected.currency} />
            <InfoRow label="Plan ales" value={`Plan ${selected.planName || selected.plan}`} />
            <InfoRow label="Trial până la" value={selected.trialEnds ? new Date(selected.trialEnds).toLocaleDateString("ro-RO") : "—"} />
            <InfoRow label="Înregistrat la" value={new Date(selected.createdAt).toLocaleDateString("ro-RO")} />

            <div style={{ borderTop: "1px solid #f1f5f9", margin: "18px 0" }} />

            {/* Link to restaurant */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#475569", display: "block", marginBottom: 6 }}>Linkează la restaurant din Hub</label>
              <select value={linkId} onChange={e => setLinkId(e.target.value)} style={{ width: "100%", height: 42, border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "0 12px", fontSize: 13, fontFamily: "inherit", outline: "none", background: "white" }}>
                <option value="">— Fără link —</option>
                {restaurants.map(r => (
                  <option key={r._id} value={r._id}>{r.name} ({r.slug})</option>
                ))}
              </select>
            </div>

            {selected.restaurantId && (
              <div style={{ background: "#eff6ff", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "#1e3a8a", marginBottom: 16 }}>
                <strong>Linkat la:</strong> {selected.restaurantId.name} ({selected.restaurantId.slug})<br/>
                <span style={{ fontSize: 12, opacity: .8 }}>Status billing: {selected.restaurantId.billingStatus || "—"} · {selected.restaurantId.currentAmount || 0} {selected.restaurantId.currentCurrency}</span>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button onClick={handleLink} disabled={saving || !linkId} style={{ height: 42, background: "#1d4ed8", color: "white", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: saving || !linkId ? "not-allowed" : "pointer", opacity: saving || !linkId ? 0.6 : 1 }}>
                {saving ? "Se salvează..." : "Linkează și activează"}
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => handleActivate(true)} disabled={saving || selected.isActive} style={{ flex: 1, height: 38, background: selected.isActive ? "#f0fdf4" : "#f0fdf4", color: "#16a34a", border: "1.5px solid #bbf7d0", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: selected.isActive ? "not-allowed" : "pointer", opacity: selected.isActive ? 0.5 : 1 }}>
                  Activează
                </button>
                <button onClick={() => handleActivate(false)} disabled={saving || !selected.isActive} style={{ flex: 1, height: 38, background: "#fff1f2", color: "#e11d48", border: "1.5px solid #fecdd3", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: !selected.isActive ? "not-allowed" : "pointer", opacity: !selected.isActive ? 0.5 : 1 }}>
                  Dezactivează
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
      <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{value || "—"}</span>
    </div>
  );
}
